import { SEO } from "components/core/seo"
import { GetStaticProps, NextPage } from "next"
import React, { useState } from "react"
import { PageTitle } from "components/core/pageTitle"
import { Container } from "components/core/container"
import { useRouter } from "next/router"
import { useUser } from "lib/supabase/auth"
import { PricingItem } from "components/pricing/pricingItem"
import { Stripe } from "stripe"
import { getProducts, getPrices } from "lib/stripe/ssrData"
import { BASE_URL, getPriceDisplay } from "lib/utils"
import { useCheckout } from "lib/stripe/useCheckout"
import classNames from "classnames"
import { Link } from "components/core/link"
import { RoundedDivider } from "components/core/divider"

interface Props {
  products: Stripe.Product[]
  prices: Stripe.Price[]
}

const UpgradePage: NextPage<Props> = ({ products, prices }) => {
  const { user } = useUser()
  const { push } = useRouter()
  const { mutateAsync: handleCheckout, isLoading } = useCheckout()
  const [billingInterval, setBillingInterval] = useState("month")
  const proProduct = products.find((p) => p.name === "Pro")
  const proMonthlyPrice = prices.find((p) => p.product === proProduct?.id && p.recurring?.interval === "month")
  const proAnnualPrice = prices.find((p) => p.product === proProduct?.id && p.recurring?.interval === "year")
  const proPrice = billingInterval === "month" ? proMonthlyPrice : proAnnualPrice

  return (
    <>
      <Container>
        <SEO
          title="Upgrade"
          description="Simple and transparent pricing that satisfy both hobby and professional needs."
          ogImage={{
            title: "Dashboard",
            url: `${BASE_URL}/upgrade`,
          }}
        />
        <PageTitle
          title="Upgrade your plan"
          subtitle="Make your developer life more efficient and gain acess to all features!"
        />
        <div className="sm:flex sm:flex-col sm:align-center">
          <div className="relative self-center bg-gray-200 dark:bg-black bg-opacity-75 dark:bg-opacity-10 rounded-3xl p-1 flex">
            <div className="hidden dark:block absolute w-full h-full left-1/2 -translate-x-1/2 bg-gradient-to-b from-violet-600 to-gray-[#151d2c] opacity-60 blur-3xl"></div>
            <button
              type="button"
              onClick={() => setBillingInterval("month")}
              className={classNames(
                "relative w-1/2 transition-all duration-300 rounded-3xl py-3 text-sm font-medium text-gray-900 dark:text-gray-100 whitespace-nowrap focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:z-10 sm:w-auto sm:px-8",
                {
                  "bg-white dark:bg-indigo-600": billingInterval === "month",
                }
              )}
            >
              Monthly
            </button>
            <button
              type="button"
              onClick={() => setBillingInterval("year")}
              className={classNames(
                "relative w-1/2 transition-all duration-300 rounded-3xl py-2 text-sm font-medium text-gray-900 dark:text-gray-100 whitespace-nowrap focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:z-10 sm:w-auto sm:px-8",
                {
                  "bg-white dark:bg-indigo-600": billingInterval === "year",
                }
              )}
            >
              Annual
            </button>
          </div>
        </div>
        <div className="grid gap-5 md:grid-cols-2 mt-12">
          <PricingItem
            hideSubscribeButton
            name="Hobby"
            priceDisplay="Free"
            description="The essentials to boost your efficiency"
            includedFeatures={[
              "10+ Built-in Widgets",
              "Custom Widgets",
              "20 Widgets per Dashboard",
              "2 Shareable Dashboards",
            ]}
            excludedFeatures={["AI enhanced Widgets", "Private Dashboards", "Password Protected Dashboards"]}
            onClick={() => {
              if (user) {
                push("/dashboard")
              } else {
                push("/auth/registration")
              }
            }}
          />
          <PricingItem
            isLoading={isLoading}
            name="Pro"
            priceDisplay={proPrice?.unit_amount ? getPriceDisplay(proPrice.unit_amount / 100, proPrice.currency) : "-"}
            description="For users who want to do more"
            includedFeatures={[
              "10+ Built-in Widgets",
              "Custom Widgets",
              "∞ Widgets per Dashboard",
              "10 Shareable Dashboards",
              "AI enhanced Widgets",
              "Private Dashboards",
              "Password Protected Dashboards",
            ]}
            priceSaved={
              billingInterval === "year" && proMonthlyPrice?.unit_amount && proAnnualPrice?.unit_amount
                ? `Save ${getPriceDisplay(
                    (proMonthlyPrice.unit_amount * 12 - proAnnualPrice.unit_amount) / 100,
                    proAnnualPrice.currency
                  )}`
                : undefined
            }
            onClick={() => {
              if (user && proPrice) {
                handleCheckout(proPrice.id)
              }
            }}
          />
        </div>
      </Container>

      <div className="bg-white dark:bg-[#151d2c] relative overflow px-4">
        <RoundedDivider className="text-gray-100 dark:text-gray-900" />
        <div className="mt-12 py-16 lg:pt-20 max-w-5xl mx-auto">
          <h1 className="text-2xl md:text-4xl font-bold text-gray-800 dark:text-gray-200 mb-6 md:mb-12">
            Frequently asked questions
          </h1>
          <div className="grid md:grid-cols-2 gap-6 md:gap-12">
            <div>
              <h3 className="mb-2 text-xl font-bold">Can I cancel a subscription at any time?</h3>
              <p className="text-gray-500 dark:text-gray-400">
                You can cancel your subscription at any time without any hassle.
              </p>
            </div>
            <div>
              <h3 className="mb-2 text-xl font-bold">Which payment methods do you accept?</h3>
              <p className="text-gray-500 dark:text-gray-400">
                We accept all major credit and debit cards. Your payment info is stored and processed securely by Stripe
                and never touches our servers.
              </p>
            </div>
            <div>
              <h3 className="mb-2 text-xl font-bold">Are there new built-in widgets coming?</h3>
              <p className="text-gray-500 dark:text-gray-400">
                Yes! We have a big list of new widgets that we would like to integrate. We are also constantly working
                on improving the existing widgets with new features.
              </p>
            </div>
            <div>
              <h3 className="mb-2 text-xl font-bold">Can I refund my money?</h3>
              <p className="text-gray-500 dark:text-gray-400">
                All of us have bought products that didn&apos;t meet our expectations. That&apos;s why we offer refunds
                for the pro plan. To refund your purchase, please contact support@fetoolkit.io before the completion of
                24 hours after your purchase.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-gray-100 dark:bg-gray-900 relative overflow">
        <RoundedDivider className="text-white dark:text-[#151d2c]" />
        <Container>
          <div className="relative text-center z-20 mx-auto px-4 max-w-3xl sm:px-6 lg:px-12 lg:max-w-7xl py-6 lg:pt-12">
            <p className="text-3xl text-gray-900 dark:text-gray-100 font-extrabold tracking-tight sm:text-4xl mb-8">
              Still have questions?
            </p>
            <Link to="mailto:info@fetoolkit.io" variant="secondary" size="big">
              Get in touch
            </Link>
          </div>
        </Container>
      </div>
    </>
  )
}

export const getStaticProps: GetStaticProps<Props> = async () => {
  const products = await getProducts()
  const prices = await getPrices()

  return {
    props: {
      products,
      prices,
    },
  }
}

export default UpgradePage
